import React from 'react';
import { ExternalLink, Terminal } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { getTranslations } from '../translations/translations';
import { LanguageCode } from '../contexts/LanguageContext';

interface Project {
  title: string;
  description: Record<LanguageCode, string>;
  tags: string[];
  url?: string;
  brandUrl?: string;
  featured?: boolean;
  category: string;
  status: string;
}

const projectsData: Project[] = [
  {
    title: 'Rise of Digital',
    description: {
      en: 'Co-founded a creative digital agency providing end-to-end solutions in software development, AI-powered process automation, and digital content production. Services include custom software solutions from landing pages to AI-driven workflow automation, 2D/3D animation & motion design, corporate identity & publication design, AI-powered advertising content production, and social media management. Serving clients across various industries with a unified creative and technical pipeline.',
      tr: 'Yazılım geliştirme, AI destekli süreç otomasyonu ve dijital içerik üretimi alanlarında uçtan uca çözümler sunan kreatif bir dijital ajansın kurucu ortağıyım. Hizmetlerimiz arasında landing page\'lerden AI destekli iş akışı otomasyonuna kadar özel yazılım çözümleri, 2D/3D animasyon ve motion design, kurumsal kimlik ve yayın tasarımı, AI destekli reklam içeriği üretimi ve sosyal medya yönetimi yer almaktadır. Farklı sektörlerden müşterilere birleşik kreatif ve teknik süreçlerle hizmet veriyoruz.',
    },
    tags: ['Software Development', 'AI Production', '2D/3D Animation', 'Motion Design', 'Corporate Identity', 'Social Media'],
    url: 'https://riseof.digital',
    brandUrl: 'https://riseof.digital',
    category: 'AGENCY',
    status: 'ACTIVE',
  },
  {
    title: 'Softinya LMS',
    description: {
      en: 'An enterprise-grade Learning Management System serving corporate clients across diverse industries. Provides end-to-end training lifecycle management including course creation, SCORM-compliant content delivery, live session scheduling, certification tracking, and advanced analytics. Served as Software & Database Specialist and AI Engineer — contributed to backend architecture, database design and optimization, and AI-powered features such as automated reporting, speech evaluation, and intelligent content recommendations.',
      tr: 'Farklı sektörlerden kurumsal müşterilere hizmet veren kapsamlı bir Öğrenme Yönetim Sistemi. Eğitim oluşturma, SCORM uyumlu içerik dağıtımı, canlı oturum planlama, sertifika takibi ve gelişmiş analitik dahil uçtan uca eğitim yaşam döngüsü yönetimi sunar. Software & Database Specialist ve AI Engineer rolleriyle görev aldım — backend mimarisi, veritabanı tasarımı ve optimizasyonu ile otomatik raporlama, konuşma değerlendirme ve akıllı içerik önerileri gibi AI destekli özelliklerin geliştirilmesinde aktif rol üstlendim.',
    },
    tags: ['PHP/Laravel', 'MySQL', 'REST API', 'AI/LLM', 'SCORM', 'Azure'],
    url: 'https://www.softinya.com',
    brandUrl: 'https://anzera.com.tr/tr',
    category: 'BACKEND',
    status: 'ACTIVE',
  },
  {
    title: 'AI English Coach',
    description: {
      en: 'An AI-powered English language coaching platform that enables real-time voice conversations with an intelligent tutor. Features text-to-speech and speech-to-text processing, real-time AI voice interaction with barge-in support, algorithmic pronunciation and fluency scoring, usage-based cost optimization, and personalized feedback to accelerate language acquisition.',
      tr: 'Akıllı bir yapay zeka eğitmeniyle gerçek zamanlı sesli konuşma imkanı sunan AI destekli İngilizce dil koçluğu platformu. Text-to-speech ve speech-to-text işleme, barge-in destekli gerçek zamanlı AI sesli etkileşim, algoritmik telaffuz ve akıcılık puanlama, kullanım bazlı maliyet optimizasyonu ve dil öğrenimini hızlandıran kişiselleştirilmiş geri bildirim özellikleri içerir.',
    },
    tags: ['AI/LLM', 'TTS', 'STT', 'Real-time Voice', 'REST API'],
    brandUrl: 'https://anzera.com.tr/tr',
    category: 'AI',
    status: 'ACTIVE',
  },
  {
    title: 'AI Chatbot Integration',
    description: {
      en: 'A platform-specific AI chatbot built on a custom RAG/Vector architecture, trained with a dedicated knowledge base covering all platform features and functionalities. The chatbot serves a dual purpose: answering user questions about the platform in natural language and enabling users to generate and retrieve platform reports directly through the conversational interface.',
      tr: 'Platformun tüm özellik ve işlevlerini kapsayan özel bir bilgi tabanıyla beslenen, RAG/Vector mimarisi üzerine inşa edilmiş platforma özgü AI chatbot çözümü. Chatbot iki temel işlevi bir arada sunar: kullanıcıların platform hakkındaki sorularını doğal dilde yanıtlamak ve platformun sunduğu raporları doğrudan sohbet arayüzü üzerinden oluşturup kullanıcıya iletmek.',
    },
    tags: ['AI/LLM', 'RAG', 'Vector DB', 'PHP/Laravel', 'REST API'],
    brandUrl: 'https://anzera.com.tr/tr',
    category: 'AI',
    status: 'ACTIVE',
  },
  {
    title: 'Çevirbil',
    description: {
      en: 'A gamification-based assessment & evaluation web application for corporate brands. Features customizable branding, leaderboards, joker mechanics, timed challenges, SCORM 2004 integration, and detailed question/category-based reporting. Includes a full admin dashboard where all end-user screens are fully configurable. Built to make corporate learning engaging and measurable.',
      tr: 'Kurumsal markalar için gamification tabanlı ölçme ve değerlendirme web uygulaması. Özelleştirilebilir kurumsal kimlik, sıralama tabloları, joker mekanikleri, zamana karşı yarış, SCORM 2004 entegrasyonu ve detaylı soru/kategori bazlı raporlama özellikleri sunar. Son kullanıcı ekranlarının tamamının yönetilebildiği kapsamlı bir admin paneline sahiptir. Kurumsal öğrenmeyi eğlenceli ve ölçülebilir hale getirmek için geliştirildi.',
    },
    tags: ['PHP/Laravel', 'MySQL', 'jQuery', 'SCORM 2004', 'Gamification', 'REST API'],
    url: 'https://cevirbil.com',
    brandUrl: 'https://anzera.com.tr/tr',
    category: 'FULLSTACK',
    status: 'ACTIVE',
  },
  {
    title: 'TAGO',
    description: {
      en: 'A real-time interactive assessment & evaluation web application powered by WebSocket. Supports live quizzes with instant participant interaction, game creation/update/clone/delete workflows, and detailed reporting by user, game, and question. Features a full admin dashboard for managing all end-user screens — everything the user sees is configurable from the admin panel.',
      tr: 'WebSocket ile anlık etkileşimli ölçme ve değerlendirme sınavları hazırlanabilen web uygulaması. Canlı sınavlarda katılımcılarla anlık etkileşim, oyun oluşturma/güncelleme/klonlama/silme iş akışları ve kullanıcı/oyun/soru bazlı detaylı raporlama sunar. Son kullanıcı ekranındaki her şeyin admin panelinden yönetilebildiği kapsamlı bir yönetim paneline sahiptir.',
    },
    tags: ['PHP/Laravel', 'MySQL', 'WebSocket', 'jQuery', 'REST API'],
    brandUrl: 'https://anzera.com.tr/tr',
    category: 'FULLSTACK',
    status: 'ACTIVE',
  },
  {
    title: 'Yarım Elma',
    description: {
      en: 'A CMS-based corporate website developed for an HR consulting and coaching firm. The platform showcases the company\'s service portfolio including employee experience training, executive coaching, leadership development programs, and corporate events. Built with a custom content management system enabling the client to manage all pages, services, and media content independently.',
      tr: 'İK danışmanlık ve koçluk firması için geliştirilen CMS tabanlı kurumsal web sitesi. Platform, çalışan deneyimi eğitimi, yönetici koçluğu, liderlik gelişim programları ve kurumsal etkinlikler dahil şirketin hizmet portföyünü sergiler. Müşterinin tüm sayfa, hizmet ve medya içeriklerini bağımsız olarak yönetebilmesini sağlayan özel bir içerik yönetim sistemiyle geliştirildi.',
    },
    tags: ['PHP/Laravel', 'Next.js', 'React', 'CMS', 'Full-Stack', 'MySQL', 'Cloudflare Security'],
    brandUrl: 'https://yarim-elma.com',
    category: 'FULLSTACK',
    status: 'ACTIVE',
  },
  {
    title: 'Çalışan Deneyimi Zirvesi',
    description: {
      en: 'A landing page website developed for Turkey\'s largest Employee Experience Summit, an annual event hosting 500+ HR professionals. Features iyzico payment gateway integration for seamless ticket purchases, Google Sheets registration integration for real-time attendee tracking, and a fully responsive design showcasing the event program, speaker lineup, and pricing tiers.',
      tr: 'Türkiye\'nin en kapsamlı Çalışan Deneyimi Zirvesi için geliştirilen landing page web sitesi. 500+ İK profesyonelinin katıldığı yıllık etkinlik için iyzico ödeme altyapısı entegrasyonu ile kesintisiz bilet satışı, Google Sheets kayıt entegrasyonu ile anlık katılımcı takibi ve etkinlik programı, konuşmacı kadrosu ile fiyatlandırma seçeneklerini sergileyen tam responsive tasarım sunar.',
    },
    tags: ['Next.js', 'React', 'iyzico', 'Google Sheets API', 'Full-Stack', 'Cloudflare'],
    brandUrl: 'https://calisandeneyimizirvesi.com',
    category: 'FULLSTACK',
    status: 'ACTIVE',
  },
  {
    title: 'Akıllı Yorum',
    description: {
      en: 'An AI-powered SaaS platform for automated review management across Google Business Profile and Instagram. Generates brand-consistent, personalized responses with 95%+ accuracy, supports multi-location management from a single dashboard, and offers multilingual capabilities. Features human-in-the-loop approval workflow, Google OAuth 2.0 authentication, and SSL-encrypted data security. Currently in beta.',
      tr: 'Google Business Profile ve Instagram yorumlarını yapay zeka ile otomatik yöneten SaaS platformu. %95+ doğruluk oranıyla markaya uygun, kişiselleştirilmiş yanıtlar üretir, tek panelden çoklu lokasyon yönetimi sunar ve çok dilli destek sağlar. Yanıtların yayınlanmadan önce incelenebildiği onay iş akışı, Google OAuth 2.0 kimlik doğrulama ve SSL şifrelemeli veri güvenliği içerir. Şu an beta aşamasındadır.',
    },
    tags: ['AI/LLM', 'Next.js', 'React', 'Google API', 'Meta API', 'OAuth 2.0', 'Full-Stack', 'SaaS'],
    brandUrl: 'https://akilliyorum.com',
    category: 'AI',
    status: 'BETA',
  },
  {
    title: 'HAN Studio',
    description: {
      en: 'An AI-powered digital fashion model platform that generates photorealistic virtual models for fashion and e-commerce brands. The system trains custom AI models from a single face photo to ensure identity consistency, then places them in professional studio scenes with full control over pose, clothing, lighting, and background. Delivers high-resolution, brand-ready visuals — eliminating the need for physical photoshoots. Currently in local development.',
      tr: 'Moda ve e-ticaret markaları için fotorealistik sanal modeller üreten yapay zeka destekli dijital moda modeli platformu. Sistem, tek bir yüz fotoğrafından özel AI modeli eğiterek kimlik tutarlılığı sağlar, ardından modeli poz, kıyafet, aydınlatma ve arka plan üzerinde tam kontrol ile profesyonel stüdyo sahnelerine yerleştirir. Fiziksel fotoğraf çekimi ihtiyacını ortadan kaldırarak yüksek çözünürlüklü, markaya hazır görseller sunar. Şu an lokal geliştirme aşamasındadır.',
    },
    tags: ['AI/LLM', 'Image Generation', 'Python', 'Full-Stack', 'ComfyUI', '3D Web UI'],
    category: 'AI',
    status: 'IN DEV',
  },
];

const Projects = () => {
  const { language } = useLanguage();
  const t = getTranslations(language);
  const projects = projectsData;

  return (
    <section id="projects" className="bg-background text-text py-24 relative overflow-hidden">
      {/* Dot matrix background */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute inset-0" style={{
          backgroundImage: 'radial-gradient(circle, rgba(0, 255, 255, 0.3) 1px, transparent 1px)',
          backgroundSize: '20px 20px'
        }}></div>
      </div>

      <div className="container mx-auto px-8 md:px-16 relative z-10">
        {/* Section header */}
        <div className="mb-20">
          <div className="flex items-center gap-4 mb-4">
            <div className="w-1 h-12 bg-gradient-to-b from-secondary to-transparent"></div>
            <div>
              <div className="text-secondary/60 text-xs mono-text tracking-widest uppercase mb-1">
                <span className="text-primary">&gt;</span> SECTION_04
              </div>
              <h2 className="text-4xl md:text-5xl font-black text-cyber-glow cyber-title">
                [{t.projects.title}]
              </h2>
            </div>
          </div>
        </div>

        {/* Projects Header */}
        <div className="flex items-center gap-4 mb-8">
          <Terminal className="text-primary w-6 h-6" />
          <h3 className="text-2xl font-bold text-primary cyber-title mono-text">
            // {language === 'en' ? 'FEATURED_PROJECTS' : 'ÖNE_ÇIKAN_PROJELER'}
          </h3>
          <div className="flex-1 h-px bg-gradient-to-r from-primary/30 to-transparent"></div>
        </div>

        {/* All Projects */}
        <div className="space-y-8">
          {projects.map((project, idx) => (
            <div key={project.title} className="cyber-card p-0 overflow-hidden relative group/featured">
              {/* Holographic corners */}
              <div className="absolute top-0 left-0 w-8 h-8 border-l-4 border-t-4 border-primary z-10"></div>
              <div className="absolute top-0 right-0 w-8 h-8 border-r-4 border-t-4 border-secondary z-10"></div>
              <div className="absolute bottom-0 left-0 w-8 h-8 border-l-4 border-b-4 border-accent z-10"></div>
              <div className="absolute bottom-0 right-0 w-8 h-8 border-r-4 border-b-4 border-primary z-10"></div>

              <div className="p-8 md:p-12 space-y-6 bg-gradient-to-br from-background/90 to-surface/90 backdrop-blur-sm">
                {/* Status bar */}
                <div className="flex items-center justify-between text-xs mono-text">
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 bg-primary rounded-full animate-pulse"></div>
                    <span className="text-primary">{project.status}</span>
                  </div>
                  <span className="text-text/40">// {project.category}</span>
                </div>

                {/* Title */}
                <h4 className="text-3xl md:text-4xl font-black text-primary cyber-title">
                  {project.title}
                </h4>

                {/* Description */}
                <p className="text-lg text-text/80 leading-relaxed">
                  {project.description[language]}
                </p>

                {/* Tags */}
                <div className="flex flex-wrap gap-2">
                  {project.tags.map((tag) => (
                    <span
                      key={tag}
                      className="cyber-card px-4 py-2 text-xs mono-text text-primary/80 hover:text-primary hover:shadow-[0_0_15px_rgba(0,255,255,0.5)] transition-all cursor-default"
                    >
                      [{tag}]
                    </span>
                  ))}
                </div>

                {/* Brand Link */}
                {project.brandUrl && (
                  <div className="pt-2">
                    <a
                      href={project.brandUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 px-5 py-2.5 border border-secondary/50 text-secondary hover:bg-secondary hover:text-black mono-text text-xs transition-all duration-300 hover:shadow-[0_0_15px_rgba(255,0,255,0.4)]"
                    >
                      <ExternalLink size={14} />
                      {language === 'en' ? 'Developed for Brand' : 'Geliştirilen Marka'}
                    </a>
                  </div>
                )}

              </div>
            </div>
          ))}
        </div>

        {/* Terminal output */}
        <div className="mt-16 cyber-card p-6 max-w-2xl mx-auto">
          <div className="mono-text text-xs space-y-2">
            <div className="text-primary/60">
              <span className="text-secondary">&gt;</span> {language === 'en'
                ? `system.log: ${projects.length} projects loaded`
                : `sistem.log: ${projects.length} proje yüklendi`}
            </div>
            <div className="text-text/40 flex items-center gap-2">
              <span className="text-secondary">&gt;</span>
              <span className="animate-pulse">_</span>
            </div>
          </div>
        </div>
      </div>

      {/* CSS for slow scan animation */}
      <style>
        {`
          @keyframes scan-slow {
            0% { transform: translateY(-100%); }
            100% { transform: translateY(100%); }
          }
          .animate-scan-slow {
            animation: scan-slow 4s linear infinite;
          }
        `}
      </style>
    </section>
  );
};

export default Projects;
